/**
 * 横串マスターの読取契約 — アクター / ドメイン / ステージ / 動詞 / 単位。
 *
 * 一覧は型ごとに 1 本（`listActors` / `listDomains` / `listVerbs` / `listUnits`）で、入力は
 * 同じ形（案件と表示言語だけ）、出力も同じ形である。詳細は `getMasterDetail` の 1 本で、
 * 種別を入力で受ける。ID は number である。
 *
 * **欄は型ごとに並びを変えず、1 本の配列で返す。** 型ごとに別の出力の形にすると、画面が
 * 型の数だけ描き分けを持つことになり、欄が 1 つ増えるたびに契約と画面の両方を直す羽目になる。
 * 欄名と出し方（本文 / 参照 / 選択肢）を値として持たせれば、描き分けは出し方 4 種で済む。
 *
 * **値が無い欄も配列に残る。** 復元が未設定のまま戻した欄（アクターの `subject` / `qualifier`、
 * ドメインの `subject`）を落とすと、読み手には「この型にその欄が無い」のか「欄はあるが
 * 未設定」なのかが見分けられない。空の欄を残し、画面が「未設定」と描く。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 横串マスターの一覧と 1 件の詳細（欄の全部・逆引き・ステージの前後）の入出力契約
 * @graph-connects none
 */
import { z } from 'zod';
import { LocaleInputShape, MasterDetailTypeSchema, SpecNodeTypeSchema, UntranslatedSchema, } from './spec-graph-common.js';
/**
 * 横串マスターの欄の出し方 4 種。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 横串マスターの欄の出し方 4 種（本文・参照・選択肢 1 つ・選択肢の並び）の閉集合
 * @graph-connects none
 */
export const MASTER_FIELD_KINDS = ['text', 'reference', 'token', 'tokens'];
/** 欄の出し方。 @graph-connects none */
export const MasterFieldKindSchema = z.enum(MASTER_FIELD_KINDS);
/** 横串マスターの要素 1 件を指す番号。 @graph-connects none */
export const MasterIdSchema = z.number().int().positive();
/**
 * 横串マスターの読取が受ける案件の指定。`before` は master、`after` は案件の未確定の変更を
 * 重ねた姿である。案件モードの左右は、画面がこの 2 面を 1 回ずつ引いて並べる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 横串マスターの読取が受ける案件の指定（番号と面）
 * @graph-connects none
 */
export const MasterPjSchema = z.object({
    number: z.number().int().positive(),
    side: z.enum(['before', 'after']),
});
/**
 * 一覧（`listActors` / `listDomains` / `listVerbs` / `listUnits`）の入力。
 *
 * 種別を持たない。種別は procedure の名前が決める — 入力で選ばせると、同じ procedure が
 * 型ごとに違う行を返すことになり、画面がどの型の行かを出力から判別できなくなる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 横串マスター一覧の入力（案件・表示言語）
 * @graph-connects none
 */
export const MasterListInputSchema = z.object({
    pj: MasterPjSchema.optional(),
    ...LocaleInputShape,
});
/**
 * 一覧の 1 行。
 *
 * `referencedCount` は「このマスターを指している要素の数」である。0 は**消してよい候補**を
 * 指すので、0 を伏せない。
 *
 * 型に固有の欄は任意で載る。動詞の行は名詞形（`nounForm`、表示言語の本文）と層（`layer`）、
 * 単位の行は表し方（`notation`）を持つ。ほかの型の行には載らない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 横串マスター一覧の 1 行（id・表示名・未翻訳・参照される数・動詞の名詞形と層・単位の表し方）
 * @graph-connects none
 */
export const MasterListItemSchema = z.object({
    id: MasterIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    referencedCount: z.number().int().nonnegative(),
    nounForm: z.string().optional(),
    layer: z.string().optional(),
    notation: z.string().optional(),
});
/**
 * 一覧の出力。
 *
 * ページングしない。型ごとの件数は最大でも 2 桁で、続きを取りに戻る形にする理由が無い。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 横串マスター一覧の出力（id の昇順の一覧）
 * @graph-connects none
 */
export const MasterListOutputSchema = z.object({
    items: z.array(MasterListItemSchema),
});
/**
 * `getMasterDetail` の入力。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `getMasterDetail` の入力（種別・id・案件・表示言語）
 * @graph-connects none
 */
export const MasterDetailInputSchema = z.object({
    type: MasterDetailTypeSchema,
    id: MasterIdSchema,
    pj: MasterPjSchema.optional(),
    ...LocaleInputShape,
});
/**
 * 詳細の欄 1 つ。
 *
 * `text` が空文字なのは「未設定」と「未翻訳」の 2 通りで、区別は `untranslated` が付ける。
 * 参照の欄（`kind: 'reference'`）では `refId` が指した先の番号で、`text` はその表示名である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 横串マスター詳細の欄 1 つ（欄名・出し方・本文・未翻訳・参照先・生値）
 * @graph-connects none
 */
export const MasterFieldSchema = z.object({
    field: z.string(),
    kind: MasterFieldKindSchema,
    text: z.string(),
    untranslated: UntranslatedSchema,
    refId: MasterIdSchema.nullable(),
    tokens: z.array(z.string()),
});
/**
 * このマスターを指している要素 1 件。
 *
 * `field` はどの欄で指しているかである。**同じ要素が 2 つの欄で指すときは 2 行になる** —
 * 畳むと逆引きから宣言へ戻れなくなる。ステージの詳細では、そのステージに属するフローが
 * ここに並ぶ（`nodeType: 'Flow'`、`field: 'stage'`）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 横串マスターを指している要素 1 件（型・id・表示名・未翻訳・どの欄か）
 * @graph-connects none
 */
export const MasterReferrerSchema = z.object({
    nodeType: SpecNodeTypeSchema,
    id: MasterIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    field: z.string(),
});
/**
 * ステージの鎖の中の前後。前は 1 つか無し、後ろは分岐のぶん複数ありうる。
 *
 * 鎖は入口の参照から読取のたびに導出するもので、保存していない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの鎖の中の前のステージと後ろのステージ
 * @graph-connects none
 */
export const MasterStageChainSchema = z.object({
    prev: MasterReferrerSchema.nullable(),
    next: z.array(MasterReferrerSchema),
});
/**
 * `getMasterDetail` の出力。
 *
 * `stageChain` はステージのときだけ値を持ち、ほかの型では `null` である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `getMasterDetail` の出力（id・型・表示名・欄の全部・逆引き・ステージの前後・最終更新時刻）
 * @graph-connects none
 */
export const MasterDetailOutputSchema = z.object({
    id: MasterIdSchema,
    type: MasterDetailTypeSchema,
    nodeType: SpecNodeTypeSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    fields: z.array(MasterFieldSchema),
    referencedBy: z.array(MasterReferrerSchema),
    stageChain: MasterStageChainSchema.nullable(),
    updatedAt: z.string().nullable(),
});
//# sourceMappingURL=spec-graph-element-masters.js.map